/**
 * Create Automation Wizard
 *
 * Multi-step dialog for creating new automations.
 * Steps:
 * 1. Select Action - What the automation does
 * 2. Select Trigger - When it fires (skipped for recurring actions)
 * 3. Name - Optional name for the automation
 */

import { faSearch } from '@fortawesome/pro-regular-svg-icons';
// eslint-disable-next-line no-restricted-imports
import { Step, StepLabel, Stepper } from '@mui/material';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'src/components/Box';
import { ButtonPropsWithLabel } from 'src/components/Dialog';
import { Dialog } from 'src/components/Dialog';
import { Icon } from 'src/components/Icon';
import { InputAdornment } from 'src/components/InputAdornment';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import {
  AUTOMATION_ACTIONS,
  getAutomationAction,
} from 'src/shared/Automation/automationActions';
import { getTriggerOptionsFromIds } from 'src/shared/Automation/automationTriggers';
import { ActionTriggerCard } from 'src/shared/Automation/components';


export interface CreateAutomationWizardProps {
  open: boolean;
  onClose: () => void;
  onAdd: (data: {
    customType: string;
    reminderType: string;
    name: string | null;
  }) => void;
}

export const CreateAutomationWizard: FC<CreateAutomationWizardProps> = ({
  open,
  onClose,
  onAdd,
}) => {
  const { t } = useTranslation();

  // Wizard state
  const [activeStep, setActiveStep] = useState(0);
  const [selectedAction, setSelectedAction] = useState<string | null>(null);
  const [selectedTrigger, setSelectedTrigger] = useState<string | null>(null);
  const [automationName, setAutomationName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Get action config for selected action
  const actionConfig = selectedAction
    ? getAutomationAction(selectedAction)
    : null;

  // Check if this is a recurring action (no triggers)
  const isRecurring = actionConfig?.triggers.length === 0;

  // Build available actions list
  const actionOptions = useMemo(() => {
    const options = Object.entries(AUTOMATION_ACTIONS)
      .map(([key, config]) => ({
        value: key,
        label: config.label,
        hasTriggers: config.triggers.length > 0,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));

    if (!searchQuery.trim()) return options;

    const query = searchQuery.toLowerCase();
    return options.filter(
      (action) =>
        action.label.toLowerCase().includes(query) ||
        t(`automations.actionDescriptions.${action.value}`, '')
          .toLowerCase()
          .includes(query)
    );
  }, [searchQuery, t]);

  // Build trigger options for selected action
  const triggerOptions = useMemo(() => {
    if (!actionConfig) return [];
    const options = getTriggerOptionsFromIds(actionConfig.triggers);

    if (!searchQuery.trim()) return options;

    const query = searchQuery.toLowerCase();
    return options.filter(
      (trigger) =>
        trigger.label.toLowerCase().includes(query) ||
        t(`automations.triggerDescriptions.${trigger.value}`, '')
          .toLowerCase()
          .includes(query)
    );
  }, [actionConfig, searchQuery, t]);

  // Determine steps based on whether action has triggers
  const steps = useMemo(() => {
    const baseSteps = [
      { key: 'action', label: t('automations.wizard.steps.action') },
    ];

    // Only include trigger step if action has triggers
    if (!selectedAction || !isRecurring) {
      baseSteps.push({
        key: 'trigger',
        label: t('automations.wizard.steps.trigger'),
      });
    }

    baseSteps.push({ key: 'name', label: t('automations.wizard.steps.name') });

    return baseSteps;
  }, [selectedAction, isRecurring, t]);

  // Get current step key
  const currentStepKey = steps[activeStep]?.key;

  // Reset wizard state
  const resetWizard = () => {
    setActiveStep(0);
    setSelectedAction(null);
    setSelectedTrigger(null);
    setAutomationName('');
    setSearchQuery('');
  };

  // Handle close
  const handleClose = () => {
    resetWizard();
    onClose();
  };

  // Handle action selection
  const handleActionSelect = (actionKey: string) => {
    setSelectedAction(actionKey);
    setSelectedTrigger(null); // Reset trigger when action changes
    setSearchQuery(''); // Reset search for next step

    const config = getAutomationAction(actionKey);
    // If recurring (no triggers), skip trigger step
    if (config?.triggers.length === 0) {
      setActiveStep(1); // Go directly to name step (which is index 1 when trigger is skipped)
    } else {
      setActiveStep(1); // Go to trigger step
    }
  };

  // Handle trigger selection
  const handleTriggerSelect = (triggerKey: string) => {
    setSelectedTrigger(triggerKey);
    setSearchQuery(''); // Reset search for next step
    setActiveStep(activeStep + 1); // Go to name step
  };

  // Handle back
  const handleBack = () => {
    if (currentStepKey === 'name' && isRecurring) {
      // If recurring, go back to action step
      setActiveStep(0);
    } else {
      setActiveStep(activeStep - 1);
    }
  };

  // Handle add
  const handleAdd = () => {
    if (!selectedAction) return;

    onAdd({
      customType: selectedAction,
      reminderType: selectedTrigger || '',
      name: automationName.trim() || null,
    });

    resetWizard();
  };

  // Check if next/create is enabled
  const canProceed = () => {
    switch (currentStepKey) {
      case 'action':
        return !!selectedAction;
      case 'trigger':
        return !!selectedTrigger;
      case 'name':
        return true; // Name is optional
      default:
        return false;
    }
  };

  // Get summary text for name step
  const getSummaryText = () => {
    if (!actionConfig) return '';

    const actionLabel = actionConfig.label;

    if (isRecurring) {
      return t('automations.wizard.summaryRecurring', { action: actionLabel });
    }

    const triggerConfig = triggerOptions.find(
      (opt) => opt.value === selectedTrigger
    );
    const triggerLabel = triggerConfig?.label || selectedTrigger;

    return t('automations.wizard.summary', {
      action: actionLabel,
      trigger: triggerLabel,
    });
  };

  // Render step content
  const renderStepContent = () => {
    switch (currentStepKey) {
      case 'action':
        return (
          <Stack spacing={2}>
            <Typography variant="h6">
              {t('automations.wizard.selectActionTitle')}
            </Typography>
            <TextField
              placeholder={t('common.actions.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon icon={faSearch} />
                  </InputAdornment>
                ),
              }}
            />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: 2,
                maxHeight: 400,
                overflowY: 'auto',
                pb: 2,
              }}
            >
              {actionOptions.map((action) => (
                <ActionTriggerCard
                  key={action.value}
                  title={action.label}
                  description={t(`automations.actionDescriptions.${action.value}`, '')}
                  onClick={() => handleActionSelect(action.value)}
                />
              ))}
            </Box>
          </Stack>
        );

      case 'trigger':
        return (
          <Stack spacing={2}>
            <Typography variant="h6">
              {t('automations.wizard.selectTriggerTitle')}
            </Typography>
            <TextField
              placeholder={t('common.actions.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              fullWidth
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon icon={faSearch} />
                  </InputAdornment>
                ),
              }}
            />
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
                gap: 2,
                maxHeight: 400,
                overflowY: 'auto',
                pb: 2,
              }}
            >
              {triggerOptions.map((trigger) => (
                <ActionTriggerCard
                  key={trigger.value}
                  title={trigger.label}
                  description={t(`automations.triggerDescriptions.${trigger.value}`, '')}
                  onClick={() => handleTriggerSelect(trigger.value)}
                />
              ))}
            </Box>
          </Stack>
        );

      case 'name':
        return (
          <Stack spacing={3}>
            <Typography variant="h6">
              {t('automations.wizard.nameTitle')}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              {getSummaryText()}
            </Typography>

            <TextField
              label={t('automations.wizard.nameLabel')}
              placeholder={t('automations.wizard.namePlaceholder')}
              value={automationName}
              onChange={(e) => setAutomationName(e.target.value)}
              fullWidth
              autoFocus
            />
          </Stack>
        );

      default:
        return null;
    }
  };

  // Build dialog actions
  const dialogActions: ButtonPropsWithLabel[] = [];

  // Back button (not on first step)
  if (activeStep > 0) {
    dialogActions.push({
      onClick: handleBack,
      label: t('common.back'),
    });
  }

  // Add button
  if (currentStepKey === 'name') {
    dialogActions.push({
      onClick: handleAdd,
      label: t('automations.wizard.addButton'),
      variant: 'prominent',
      color: 'primary',
      disabled: !canProceed(),
    });
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      title={t('automations.wizard.title')}
      maxWidth="md"
      fullWidth
      actions={dialogActions}
    >
      <Box sx={{ width: '100%', mb: 4 }}>
        <Stepper activeStep={activeStep} alternativeLabel>
          {steps.map((step) => (
            <Step key={step.key}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      {renderStepContent()}
    </Dialog>
  );
};
